import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CalendarMonth as CalendarIcon,
  ViewList as ListIcon,
  ContentCopy as DuplicateIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import JuntoApiService, { Class, Location, Room, Instructor, ClassCategory } from '../services/api';
import ResponsiveTable from '../components/ResponsiveTable/ResponsiveTable';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

const ClassesPage: React.FC = () => {
  // State
  const [classes, setClasses] = useState<Class[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [classCategories, setClassCategories] = useState<ClassCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [daysFilter, setDaysFilter] = useState(7);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location_id: '',
    room_id: '',
    instructor_id: '',
    class_category_id: '',
    start_time: dayjs() as Dayjs,
    end_time: dayjs().add(1, 'hour') as Dayjs,
    difficulty_level: '',
    special_notes: '',
  });

  // Load data
  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      const [classesData, locationsData, roomsData, instructorsData, categoriesData] = await Promise.all([
        JuntoApiService.getClasses(includeInactive), // TODO: API doesn't support date filtering yet
        JuntoApiService.getLocations(includeInactive),
        JuntoApiService.getRooms(includeInactive),
        JuntoApiService.getInstructors(includeInactive).catch(() => []), // May not be implemented yet
        JuntoApiService.getClassCategories(includeInactive),
      ]);
      
      setClasses(classesData);
      setLocations(locationsData);
      setRooms(roomsData);
      setInstructors(instructorsData);
      setClassCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);


  // Helper functions
  const getLocationName = (id: string) => locations.find(l => l.id === id)?.name || 'Unknown';
  const getRoomName = (id: string) => rooms.find(r => r.id === id)?.name || 'Unknown';
  const getInstructorName = (id: string) => instructors.find(i => i.id === id)?.name || 'Unknown';
  const getCategoryName = (id: string) => classCategories.find(c => c.id === id)?.name || 'Unknown';

  // Filter classes based on search term
  const filteredClasses = classes.filter(cls =>
    cls.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getLocationName(cls.location_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
    getInstructorName(cls.instructor_id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const paginatedClasses = filteredClasses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Dialog handlers
  const openCreateDialog = () => {
    setEditingClass(null);
    setFormData({
      name: '',
      description: '',
      location_id: '',
      room_id: '',
      instructor_id: '',
      class_category_id: '',
      start_time: dayjs(),
      end_time: dayjs().add(1, 'hour'),
      difficulty_level: '',
      special_notes: '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (cls: Class) => {
    setEditingClass(cls);
    setFormData({
      name: cls.name,
      description: cls.description || '',
      location_id: cls.location_id,
      room_id: cls.room_id,
      instructor_id: cls.instructor_id,
      class_category_id: cls.class_category_id,
      start_time: dayjs(cls.start_time),
      end_time: dayjs(cls.end_time),
      difficulty_level: cls.difficulty_level || '',
      special_notes: cls.special_notes || '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleDuplicate = (cls: Class) => {
    setEditingClass(null); // Set to null so it creates a new class
    setFormData({
      name: cls.name,
      description: cls.description || '',
      location_id: cls.location_id,
      room_id: cls.room_id,
      instructor_id: cls.instructor_id,
      class_category_id: cls.class_category_id,
      start_time: dayjs(cls.start_time),
      end_time: dayjs(cls.end_time),
      difficulty_level: cls.difficulty_level || '',
      special_notes: cls.special_notes || '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingClass(null);
    setDialogError(null);
  };

  const handleSave = async () => {
    try {
      setDialogError(null);
      
      const classData = {
        ...formData,
        start_time: formData.start_time.format('YYYY-MM-DDTHH:mm:ss'),
        end_time: formData.end_time.format('YYYY-MM-DDTHH:mm:ss'),
        id: editingClass?.id || `class-${Date.now()}`,
        created_at: editingClass?.created_at || new Date().toISOString(),
      };

      if (editingClass) {
        // Update existing class
        await JuntoApiService.updateClass(classData as Class);
      } else {
        // Create new class
        await JuntoApiService.createClass(classData);
      }
      
      await loadAllData();
      closeDialog();
    } catch (err) {
      console.error('Failed to save class:', err);
      setDialogError('Failed to save class. Please check your input.');
    }
  };

  const getRoomsForLocation = (locationId: string) => {
    return rooms.filter(room => room.location_id === locationId);
  };

  // Calendar event helpers
  const getCalendarEvents = () => {
    return filteredClasses.map(cls => {
      const location = locations.find(l => l.id === cls.location_id);
      const room = rooms.find(r => r.id === cls.room_id);
      const instructor = instructors.find(i => i.id === cls.instructor_id);
      const category = classCategories.find(c => c.id === cls.class_category_id);
      
      // Color scheme based on event_type
      const getEventColor = () => {
        if (!cls.is_active) return '#9e9e9e';
        if (!category) return '#1976d2';
        
        switch (category.event_type) {
          case 'group': return '#2e7d32'; // green
          case 'private': return '#d32f2f'; // red
          case 'event': return '#ed6c02'; // orange
          case 'wellness': return '#9c27b0'; // purple
          default: return '#1976d2'; // blue
        }
      };
      
      const color = getEventColor();
      
      return {
        id: cls.id,
        title: cls.name,
        start: cls.start_time,
        end: cls.end_time,
        backgroundColor: color,
        borderColor: color,
        extendedProps: {
          class: cls,
          location: location?.name || 'Unknown',
          room: room?.name || 'Unknown',
          instructor: instructor?.name || 'Unknown',
          category: category?.name || 'Unknown',
          description: cls.description,
          isActive: cls.is_active,
        },
        display: 'block',
        classNames: !cls.is_active ? ['inactive-class'] : [],
      };
    });
  };

  const handleEventClick = (info: any) => {
    const cls = info.event.extendedProps.class;
    openEditDialog(cls);
  };

  const handleDateClick = (info: any) => {
    // Round to nearest 10 minutes when clicking on calendar
    const clickedDate = dayjs(info.date);
    const minutes = clickedDate.minute();
    const roundedMinutes = Math.round(minutes / 10) * 10;
    const roundedTime = clickedDate.minute(roundedMinutes).second(0);
    
    // Open create dialog with the clicked time
    setEditingClass(null);
    setFormData({
      name: '',
      description: '',
      location_id: '',
      room_id: '',
      instructor_id: '',
      class_category_id: '',
      start_time: roundedTime,
      end_time: roundedTime.add(1, 'hour'),
      difficulty_level: '',
      special_notes: '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading classes...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Classes</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Showing {daysFilter === 1 ? 'today' : daysFilter === 3 ? 'next 3 days' : daysFilter === 7 ? 'next week' : daysFilter === 14 ? 'next 2 weeks' : daysFilter === 30 ? 'next month' : daysFilter === 90 ? 'next 3 months' : 'next year'}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="list" aria-label="list view">
              <ListIcon />
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="calendar view">
              <CalendarIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={daysFilter}
              onChange={(e) => setDaysFilter(Number(e.target.value))}
              label="Time Range"
            >
              <MenuItem value={1}>Today</MenuItem>
              <MenuItem value={3}>Next 3 Days</MenuItem>
              <MenuItem value={7}>Next Week</MenuItem>
              <MenuItem value={14}>Next 2 Weeks</MenuItem>
              <MenuItem value={30}>Next Month</MenuItem>
              <MenuItem value={90}>Next 3 Months</MenuItem>
              <MenuItem value={365}>Next Year</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                icon={<VisibilityOffIcon />}
                checkedIcon={<VisibilityIcon />}
              />
            }
            label="Show Inactive"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Create Class
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={loadAllData} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <EventIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{classes.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Classes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <LocationIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{locations.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Locations
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{instructors.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Instructors
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TimeIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {classes.filter(c => dayjs(c.start_time).isAfter(dayjs())).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upcoming
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder="Search classes, locations, instructors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ flexGrow: 1 }}
          />
        </Box>
      </Paper>

      {/* Classes Table - Show only in list view */}
      {viewMode === 'list' && (
        <Paper>
          <ResponsiveTable>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Class Name</TableCell>
                <TableCell className="mobile-hide">Location</TableCell>
                <TableCell className="mobile-hide">Room</TableCell>
                <TableCell>Instructor</TableCell>
                <TableCell className="mobile-priority-low">Category</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell className="mobile-hide">Duration</TableCell>
                <TableCell className="mobile-priority-low">Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedClasses.map((cls) => (
                <TableRow 
                  key={cls.id} 
                  hover 
                  sx={{ 
                    opacity: cls.is_active ? 1 : 0.6,
                    backgroundColor: cls.is_active ? 'inherit' : 'action.hover'
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {cls.name}
                      </Typography>
                      {cls.description && (
                        <Typography variant="body2" color="text.secondary">
                          {cls.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell className="mobile-hide">{getLocationName(cls.location_id)}</TableCell>
                  <TableCell className="mobile-hide">{getRoomName(cls.room_id)}</TableCell>
                  <TableCell>{getInstructorName(cls.instructor_id)}</TableCell>
                  <TableCell className="mobile-priority-low">
                    <Chip 
                      label={getCategoryName(cls.class_category_id)} 
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {dayjs(cls.start_time).format('MMM DD, YYYY HH:mm')}
                  </TableCell>
                  <TableCell className="mobile-hide">
                    {Math.round(dayjs(cls.end_time).diff(dayjs(cls.start_time), 'minutes'))} min
                  </TableCell>
                  <TableCell className="mobile-priority-low">
                    <Chip
                      label={cls.is_active ? 'Active' : 'Inactive'}
                      color={cls.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(cls)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton
                          size="small"
                          onClick={() => handleDuplicate(cls)}
                        >
                          <DuplicateIcon />
                        </IconButton>
                      </Tooltip>
                      <EntityActionButtons
                        entity={cls}
                        entityType="classes"
                        entityDisplayName="Class"
                        onEdit={openEditDialog}
                        onRefresh={loadAllData}
                        canEdit={false}
                        canViewHistory={false}
                        renderEntityInHistory={(entity) => (
                          <Box>
                            <Typography variant="body1">
                              <strong>Name:</strong> {entity.name}
                            </Typography>
                            <Typography variant="body1">
                              <strong>Location:</strong> {getLocationName(entity.location_id)}
                            </Typography>
                            <Typography variant="body1">
                              <strong>Instructor:</strong> {getInstructorName(entity.instructor_id)}
                            </Typography>
                            <Typography variant="body1">
                              <strong>Time:</strong> {dayjs(entity.start_time).format('MMM DD, YYYY HH:mm')}
                            </Typography>
                          </Box>
                        )}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ResponsiveTable>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredClasses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
        </Paper>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Paper sx={{ p: 2 }}>
          <style>{`
            .fc-event {
              cursor: pointer;
              padding: 2px 4px;
              border-radius: 4px;
              font-size: 0.875rem;
            }
            .fc-event-title {
              font-weight: 500;
            }
            .fc-event.inactive-class {
              opacity: 0.6;
              text-decoration: line-through;
            }
            .fc-timegrid-slot {
              height: 40px;
            }
            .fc-timegrid-slot-minor {
              border-style: dotted;
            }
            .fc-col-header-cell {
              background-color: #f5f5f5;
            }
            .fc-event-time {
              font-size: 0.75rem;
            }
            .fc-event-main {
              padding: 2px;
            }
            .fc-timegrid-event-harness-inset .fc-timegrid-event {
              box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.12);
            }
            /* Handle overlapping events */
            .fc-timegrid-event-harness {
              margin-right: 2px;
            }
            .fc-direction-ltr .fc-timegrid-col-events {
              margin: 0 2px;
            }
          `}</style>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            events={getCalendarEvents()}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            slotDuration="00:10:00"
            slotLabelInterval="01:00:00"
            allDaySlot={false}
            weekends={true}
            height="auto"
            eventMaxStack={3}
            dayMaxEvents={true}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
            eventContent={(eventInfo) => {
              const { event } = eventInfo;
              const { extendedProps } = event;
              return (
                <Box sx={{ p: 0.5, overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                    {event.title}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9 }}>
                    {extendedProps.room} • {extendedProps.instructor}
                  </Typography>
                </Box>
              );
            }}
          />
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Dialog 
          open={dialogOpen} 
          onClose={(event, reason) => {
            if (reason !== 'backdropClick') {
              closeDialog();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              closeDialog();
            }
          }}
          maxWidth="md" 
          fullWidth>
          <DialogTitle>
            {editingClass ? 'Edit Class' : 'Create New Class'}
          </DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Class Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Class Category</InputLabel>
                  <Select
                    value={formData.class_category_id}
                    onChange={(e) => setFormData({ ...formData, class_category_id: e.target.value })}
                  >
                    {classCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={formData.location_id}
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        location_id: e.target.value,
                        room_id: '' // Reset room when location changes
                      });
                    }}
                  >
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required disabled={!formData.location_id}>
                  <InputLabel>Room</InputLabel>
                  <Select
                    value={formData.room_id}
                    onChange={(e) => setFormData({ ...formData, room_id: e.target.value })}
                  >
                    {getRoomsForLocation(formData.location_id).map((room) => (
                      <MenuItem key={room.id} value={room.id}>
                        {room.name} (Capacity: {room.capacity})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Instructor</InputLabel>
                  <Select
                    value={formData.instructor_id}
                    onChange={(e) => setFormData({ ...formData, instructor_id: e.target.value })}
                  >
                    {instructors.map((instructor) => (
                      <MenuItem key={instructor.id} value={instructor.id}>
                        {instructor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Difficulty Level</InputLabel>
                  <Select
                    value={formData.difficulty_level}
                    onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="beginner">Beginner</MenuItem>
                    <MenuItem value="intermediate">Intermediate</MenuItem>
                    <MenuItem value="advanced">Advanced</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Start Time"
                  value={formData.start_time}
                  onChange={(newValue: any) => {
                    if (newValue) {
                      setFormData({ 
                        ...formData, 
                        start_time: newValue,
                        end_time: newValue.add(1, 'hour')
                      });
                    }
                  }}
                  renderInput={(params: any) => <TextField {...params} fullWidth required />}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="End Time"
                  value={formData.end_time}
                  onChange={(newValue: any) => {
                    if (newValue) {
                      setFormData({ ...formData, end_time: newValue });
                    }
                  }}
                  renderInput={(params: any) => <TextField {...params} fullWidth required />}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Special Notes"
                  value={formData.special_notes}
                  onChange={(e) => setFormData({ ...formData, special_notes: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              {editingClass ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </Box>
  );
};

export default ClassesPage;